import { ColumnDef } from '@tanstack/react-table';
import { ActiveSourcingChannelDetails } from 'api-clients/monolith';
import { isPast } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReturnBackArrowIcon } from '@/components/icons/return-back-arrow';
import { ChannelStatusPill } from '@/components/base/channel-status-pill';
import { ApplicantStats } from '@/components/ui/applicant-stats';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { ConversionStats } from '@/components/ui/conversion-stats';
import { CurrencyStats } from '@/components/ui/currency-stats';
import { Duration } from '@/components/ui/duration';
import { Typography } from '@/components/ui/typography';
import { useHiringGoalIdParam } from '@/hooks/use-hiring-goal-id-param';
import { BOOST, OTHER, REACH, ZIPRECRUITER } from '@/types/channels';
import { getCampaignToggleState } from '@/utils/campaign-toggle-state';
import { isFreeChannel } from '@/utils/channel-util';
import { formatShortDate } from '@/utils/date-util';

import { ToggleCampaign } from '../toggle-campaign';
import { CampaignMenu } from './campaign-menu';
import { Timestamp } from './timestamp';
import { SetReachPublisherStatus } from './use-reach-channels';
import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { useMetricsFilterParams } from '@/hooks/use-metrics-filter-params';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/utils/classnames';
import { isNonIntegratedChannel } from '@/utils/channel-util';
interface ChannelColumnsProps {
  brand?: string;
  location?: string;
  funnel?: string;
  setReachPublisherStatus?: SetReachPublisherStatus;
}

export function useChannelColumns({
  brand,
  location,
  funnel,
  setReachPublisherStatus,
}: ChannelColumnsProps): ColumnDef<ActiveSourcingChannelDetails>[] {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const navigate = useNavigate();
  const { withHiringGoalId } = useHiringGoalIdParam();
  const [searchParams] = useSearchParams();
  const { hiringGoal } = useOpeningDetails();
  const { metricsViewMode } = useOpeningContext();
  const {
    startDate: filterStartDate,
    endDate: filterEndDate,
    filterRange,
  } = useMetricsFilterParams();
  const isDateRangeSelected =
    metricsViewMode === 'date_range' && filterStartDate && filterEndDate;
  const handleCampaignEdit = (rowItem: ActiveSourcingChannelDetails) => {
    const {
      sourcing_purchase_id: sourcingPurchaseId,
      sourcing_channel: { slug },
    } = rowItem;

    if (!sourcingPurchaseId || !slug) return undefined;

    let channelSlug = slug;
    // Reach campaigns will ideally have the publisher slug
    if (rowItem.reach_campaign_id) {
      channelSlug = REACH;
    }

    return () => {
      navigate({
        pathname: `${channelSlug}/${sourcingPurchaseId}/edit`,
        search: withHiringGoalId(searchParams),
      });
    };
  };

  const getDurationTooltipContent = (rowItem: ActiveSourcingChannelDetails) => {
    if (rowItem.sourcing_channel.slug === OTHER) {
      return undefined;
    }
    const baseContent = intl.formatMessage(
      {
        defaultMessage: 'Campaign duration: {startDate} - {endDate}',
        description: 'Actual duration of the campaign',
      },
      {
        startDate: formatShortDate(rowItem.starts_at),
        endDate: formatShortDate((rowItem.end_at || null) ?? new Date()),
      },
    );

    const overlapStartDate = rowItem.overlap_start_date ?? rowItem.starts_at;
    const overlapEndDate =
      (rowItem.overlap_end_date || null) ??
      (rowItem.end_at || null) ??
      new Date();
    const overlapDays = rowItem.duration_stats.expected;

    const overlapContent =
      metricsViewMode === 'date_range'
        ? !filterRange &&
          intl.formatMessage(
            {
              defaultMessage:
                'Overlapping duration of campaign within selected date range: {days} days ({startDate} - {endDate})',
              description:
                'Overlap duration of the campaign with the selected date range',
            },
            {
              days: overlapDays,
              startDate: formatShortDate(overlapStartDate),
              endDate: formatShortDate(overlapEndDate),
            },
          )
        : intl.formatMessage(
            {
              defaultMessage:
                'Overlapping duration of campaign within hiring goal: {days} days ({startDate} - {endDate})',
              description:
                'Overlap duration of the campaign with the hiring goal',
            },
            {
              days: overlapDays,
              startDate: formatShortDate(overlapStartDate),
              endDate: formatShortDate(overlapEndDate),
            },
          );

    if (
      !(
        rowItem.reach_campaign_id &&
        rowItem.end_at &&
        isPast(rowItem.end_at)
      ) &&
      (rowItem.overlap_start_date || rowItem.starts_at) &&
      (rowItem.overlap_end_date || rowItem.end_at)
    ) {
      return (
        <>
          {baseContent}
          {!!overlapContent && (
            <>
              <br />
              <br />
              {overlapContent}
            </>
          )}
        </>
      );
    }

    return baseContent;
  };

  const showOnlyActual = !hiringGoal?.external_id && !!isDateRangeSelected;

  return [
    {
      id: 'actions',
      cell: ({ row }) => {
        const {
          status,
          sourcing_purchase_id: sourcingPurchaseId,
          sourcing_channel: channel,
          reach_campaign_id: reachCampaignId,
          is_readonly: isReadonly,
        } = row.original;

        const isOtherChannel = !sourcingPurchaseId;

        const { showToggle, isToggleEnabled, showTooltip } =
          getCampaignToggleState({
            status,
            isReadonly,
            channelSlug: channel.slug,
            isPublishable: channel.is_publishable,
            isVonqChannel: channel.is_vonq_channel,
            isOtherChannel,
          });

        const showMenu = !isOtherChannel;

        if (row.original.reach_campaign_id)
          return <ReturnBackArrowIcon className="text-fx-neutral-400 w-9" />;

        return (
          <div
            className={cn(
              'flex h-full flex-col items-center gap-6',
              showToggle && showMenu ? 'justify-around' : 'justify-start',
            )}
          >
            {showToggle && (
              <Tooltip
                content={
                  showTooltip &&
                  intl.formatMessage({
                    defaultMessage:
                      'Pausing or resuming campaigns isn’t available yet for this channel.',
                    description:
                      'Tooltip shown when users attempt to pause or resume a campaign',
                  })
                }
              >
                <span className="h-4">
                  <ToggleCampaign
                    sourcingPurchaseId={sourcingPurchaseId ?? ''}
                    reachCampaignId={reachCampaignId ?? null}
                    status={status}
                    channel={channel.slug}
                    setReachPublisherStatus={setReachPublisherStatus}
                    isDisabled={!isToggleEnabled}
                  />
                </span>
              </Tooltip>
            )}
            {showMenu && (
              <CampaignMenu
                sourcingPurchaseId={sourcingPurchaseId ?? ''}
                reachCampaignId={reachCampaignId ?? null}
                status={status}
                channelSlug={channel.slug}
                onEdit={handleCampaignEdit(row.original)}
                setReachPublisherStatus={setReachPublisherStatus}
                isDisabled={isOtherChannel}
                showPauseResume={!!isToggleEnabled}
              />
            )}
          </div>
        );
      },
    },
    {
      id: 'sourcing_channel',
      accessorKey: 'sourcing_channel',
      cell: ({ row }) => {
        const { slug: channelSlug, ...channel } = row.original.sourcing_channel;

        if (row.original.reach_campaign_id)
          return (
            <ChannelIcon
              channelSlug={channelSlug}
              variant="large"
              size="md"
              channelName={channel.name}
            />
          );

        const isNonIntegrated = isNonIntegratedChannel(
          channel.is_integratable,
          channel.is_publishable,
          !!channel.is_vonq_channel,
        );

        return (
          <>
            <div className="flex w-full min-w-0 items-center gap-2">
              {/* No logo for Others */}
              <div className="flex w-full flex-col gap-1">
                <div className="flex items-center gap-3">
                  <ChannelIcon
                    channelSlug={channelSlug}
                    logoSquareUrl={channel.logo_square}
                    size="xl"
                    channelName={channel.name}
                  />
                  <ChannelStatusPill
                    status={row.original.status}
                    isNonIntegrated={isNonIntegrated}
                  />
                </div>
                <Typography variant="body2" className="text-fx-neutral-600">
                  {row.original.campaign_title ?? channel.name}
                </Typography>
              </div>
            </div>
          </>
        );
      },
    },
    {
      accessorKey: 'budget_stats',
      // Needs room for "$–1,000 / $1,000 🔒" + optional timestamp
      size: 160,
      cell: ({ row }) => {
        const channelSlug = row.original.sourcing_channel.slug;
        const isFree = isFreeChannel(
          row.original.sourcing_channel,
          row.original.budget_stats.expected == null,
          row.original.contract_id == null,
        );
        const endAt = row.original.end_at || null;
        const overlapEndDate = row.original.overlap_end_date || null;
        const expectedTooltipContent =
          !isDateRangeSelected &&
          intl.formatMessage(
            {
              defaultMessage:
                'Budget available during the selected date range: {budget}',
              description:
                'Stat tooltip: derived from available budget during the selected date range',
            },
            {
              budget: formatCurrency(row.original.budget_stats.expected ?? 0),
            },
          );

        const actualTooltipContent = intl.formatMessage(
          {
            defaultMessage:
              'Budget spent within the selected date range ({startDate} - {endDate}): {budget}',
            description:
              'Stat tooltip: derived from spent budget within the selected date range',
          },
          {
            budget: formatCurrency(row.original.budget_stats.actual ?? 0),
            startDate: formatShortDate(
              row.original.overlap_start_date ?? row.original.starts_at,
            ),
            endDate: formatShortDate(overlapEndDate ?? endAt ?? new Date()),
          },
        );

        return (
          <>
            {/* Title */}
            <Typography variant="caption">
              {intl.formatMessage({
                defaultMessage: 'Spend / Budget',
                description:
                  'Channels table: column caption for money spent on this channel',
              })}
            </Typography>

            {/* Content - Free / Managed outside Fountain / CurrencyStats */}
            {isFree ? (
              <Typography
                variant="small1"
                className="text-fx-neutral-600 italic"
              >
                <FormattedMessage
                  defaultMessage="Free"
                  description="Free of cost"
                />
              </Typography>
            ) : row.original.contract_id != null ? (
              <Typography
                variant="small1"
                className="text-fx-neutral-600 text-center italic"
              >
                <FormattedMessage
                  defaultMessage="Managed outside{br}Fountain"
                  description="Label for channels that are managed outside Fountain"
                  values={{ br: <br /> }}
                />
              </Typography>
            ) : (
              <CurrencyStats
                stats={row.original.budget_stats}
                onClick={
                  !row.original.is_readonly &&
                  row.original.status !== 'completed'
                    ? handleCampaignEdit(row.original)
                    : undefined
                }
                typographyVariant="large2"
                isApproximate={!row.original.sourcing_channel.is_integratable}
                showLockIcon={
                  channelSlug === ZIPRECRUITER || channelSlug === BOOST
                }
                showOnlyActual={showOnlyActual}
                actualBudgetTooltipContent={
                  expectedTooltipContent && actualTooltipContent ? (
                    <>
                      {actualTooltipContent}
                      <br />
                      <br />
                      {expectedTooltipContent}
                    </>
                  ) : (
                    <>{actualTooltipContent}</>
                  )
                }
              />
            )}

            {/* Last updated at timestamp */}
            {row.original.budget_stats.last_updated_at &&
              row.original.sourcing_channel.is_integratable && (
                <Timestamp
                  lastUpdatedAt={row.original.budget_stats.last_updated_at}
                />
              )}
          </>
        );
      },
    },
    {
      accessorKey: 'duration',
      // Needs room for date ranges like "Apr 17 - Jun 16" or the scheduled span text
      size: 164,
      cell: ({ row }) => {
        const isReachCompleted = Boolean(
          row.original.reach_campaign_id &&
          row.original.end_at &&
          isPast(row.original.end_at),
        );
        return (
          <>
            <Typography variant="caption">
              {intl.formatMessage({
                defaultMessage: 'Duration (Days)',
                description:
                  'Channels table: label for how many days the campaign runs',
              })}
            </Typography>
            <Duration
              stats={row.original.duration_stats}
              brand={brand}
              location={location}
              funnel={funnel}
              typographyVariant="large2"
              showDays={false}
              showDateRange={
                row.original.status === 'completed' || isReachCompleted
              }
              startDate={
                row.original.overlap_start_date ?? row.original.starts_at
              }
              endDate={row.original.overlap_end_date ?? row.original.end_at}
              tooltipContent={getDurationTooltipContent(row.original)}
              onAddButtonClick={handleCampaignEdit(row.original)}
              channelSlug={row.original.sourcing_channel.slug}
              showOnlyActual={
                showOnlyActual ||
                !!row.original.sourcing_channel.is_organic_channel
              }
            />
          </>
        );
      },
    },
    {
      accessorKey: 'applicants_stats',
      size: 90,
      cell: ({ row }) => (
        <>
          <Typography variant="caption">
            {intl.formatMessage({
              defaultMessage: 'Applicants',
              description:
                'Channels table: column caption for number of applicants',
            })}
          </Typography>
          <ApplicantStats
            stats={row.original.applicants_stats}
            typographyVariant="large2"
            showOnlyActual
          />
        </>
      ),
    },
    {
      accessorKey: 'cpa_stats',
      size: 70,
      cell: ({ row }) => {
        const isFree = isFreeChannel(
          row.original.sourcing_channel,
          row.original.budget_stats.expected == null,
          row.original.contract_id == null,
        );

        return (
          <>
            <Typography variant="caption">
              {intl.formatMessage({
                defaultMessage: 'CPA',
                description:
                  'Channels table: cost-per-applicant acronym (not spelled out for layout)',
              })}
            </Typography>
            {isFree ? (
              <Typography
                variant="small1"
                className="text-fx-neutral-600 italic"
              >
                <FormattedMessage
                  defaultMessage="Free"
                  description="Displayed when a Pool channel has no cost for CPA (no spend)"
                />
              </Typography>
            ) : (
              <CurrencyStats
                stats={row.original.cpa_stats}
                onClick={
                  row.original.status !== 'completed' &&
                  row.original.contract_id == null
                    ? handleCampaignEdit(row.original)
                    : undefined
                }
                typographyVariant="large2"
                separator={<Separator />}
                tooltipContent={intl.formatMessage({
                  defaultMessage:
                    'The estimated cost per applicant for this channel based on the budget and target number of applicants',
                  description:
                    'Stat tooltip: derived CPA from budget and applicant target',
                })}
                showOnlyActual
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: 'conversion_stats',
      size: 95,
      cell: ({ row }) => {
        const isFree = isFreeChannel(
          row.original.sourcing_channel,
          row.original.budget_stats.expected == null,
          row.original.contract_id == null,
        );
        return (
          <>
            <Typography variant="caption">
              {intl.formatMessage({
                defaultMessage: 'Conversion',
                description:
                  'Channels table: column caption for applicant-to-hire conversion',
              })}
            </Typography>
            <ConversionStats
              stats={row.original.conversion_stats}
              onClick={
                row.original.status !== 'completed' &&
                !isFree &&
                row.original.contract_id == null
                  ? handleCampaignEdit(row.original)
                  : undefined
              }
              typographyVariant="large2"
              separator={<Separator />}
              tooltipContent={intl.formatMessage({
                defaultMessage:
                  'The predicted conversion rate of applicants to hires based on target numbers',
                description:
                  'Stat tooltip: forecast conversion from targets, not only actuals',
              })}
              showOnlyActual
            />
          </>
        );
      },
    },
    {
      accessorKey: 'hire_stats',
      size: 70,
      cell: ({ row }) => (
        <>
          <Typography variant="caption">
            {intl.formatMessage({
              defaultMessage: 'Hires',
              description:
                'Channels table: column caption for number of hires attributed',
            })}
          </Typography>
          <ApplicantStats
            stats={row.original.hire_stats}
            typographyVariant="large2"
            showOnlyActual
          />
        </>
      ),
    },
  ];
}

function Separator() {
  return <span className="bg-fx-neutral-300 mx-1 h-6 w-px" />;
}
